import { useId } from 'react';
import { SearchSelect, type SearchOption } from '@/components/SearchSelect';
import { Field } from '@/components/ui';
import {
  contentProblem,
  evenCounts,
  fitsPrecision,
  fromTenThousandths,
  MAX_INNER_COUNT,
  MAX_PACKAGINGS,
  nearestSplits,
  packagingProblem,
  plural,
  toTenThousandths,
  type ItemPackaging,
  type PackagingInput,
  type PackingContext,
  type PackingUnit,
  type UnitFamily,
} from '@/packing';

/** Which of a package's three linked figures someone typed. */
type Linked = 'total' | 'count' | 'each';

/** One package size as the form holds it, every figure as typed. */
export interface PackRow {
  key: string;
  /** Set for a package the item already has, so an edit rewrites it in place. */
  id?: string | undefined;
  name: string;
  /** The item's unit in one package. */
  qtyPerPackage: string;
  hasInner: boolean;
  innerName: string;
  innerCount: string;
  /** The item's unit in one pack inside. */
  innerQty: string;
  isDefault: boolean;
  /** The figures typed most recently, newest first: these two fix the third. */
  edits: Linked[];
}

export interface PackingValue {
  contentQty: string;
  contentUomId: string;
  contentUomLabel: string;
  packagings: PackRow[];
}

/** The unit the item is counted in, as the form knows it. */
export interface CountingUnit extends PackingUnit {
  family: UnitFamily;
}

let rowSeq = 0;
const newKey = () => `pack-${++rowSeq}`;

export function packRowsFrom(packagings: readonly ItemPackaging[] | undefined): PackRow[] {
  return (packagings ?? []).map((p) => ({
    key: newKey(),
    id: p.id,
    name: p.name,
    qtyPerPackage: p.qtyPerPackage,
    hasInner: !!p.innerName,
    innerName: p.innerName ?? '',
    innerCount: p.innerCount?.toString() ?? '',
    innerQty: p.innerQty ?? '',
    isDefault: p.isDefault,
    edits: [],
  }));
}

/** The rows as the server takes them; a pack-inside left unticked sends nothing for it. */
export function packagingsToSend(rows: readonly PackRow[]): (PackagingInput & { id?: string })[] {
  return rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    name: r.name.trim(),
    qtyPerPackage: r.qtyPerPackage.trim(),
    innerName: r.hasInner ? r.innerName.trim() || null : null,
    innerCount: r.hasInner && r.innerCount.trim() !== '' ? Number(r.innerCount) : null,
    innerQty: r.hasInner ? r.innerQty.trim() || null : null,
    isDefault: r.isDefault,
  }));
}

/** The rows as an item's packages, for a quantity box on the same form (the reorder level). */
export function packagingsForEntry(rows: readonly PackRow[], context: PackingContext | null): ItemPackaging[] {
  if (!context) return [];
  const sent = packagingsToSend(rows);
  return sent
    .map((p, i) => ({ p, row: rows[i]! }))
    .filter(({ p }) => packagingProblem([p], context) === null)
    .map(({ p, row }) => ({
      id: row.key,
      name: p.name,
      qtyPerPackage: fromTenThousandths(toTenThousandths(p.qtyPerPackage)!),
      innerName: p.innerName ?? null,
      innerCount: p.innerCount ?? null,
      innerQty: p.innerQty ? fromTenThousandths(toTenThousandths(p.innerQty)!) : null,
      isDefault: row.isDefault || (!rows.some((r) => r.isDefault) && row === rows[0]),
    }));
}

/** The first problem with the whole card, or null; what Save refuses on. */
export function packingProblem(value: PackingValue, context: PackingContext | null): string | null {
  if (!context) return value.packagings.length ? 'Choose the unit of measure before adding package sizes.' : null;
  return (
    contentProblem(value.contentQty, value.contentUomId !== '', context) ??
    packagingProblem(packagingsToSend(value.packagings), context)
  );
}

/** A typed figure as ten-thousandths above zero, or null. */
const positive = (v: string) => {
  const n = toTenThousandths(v);
  return n !== null && n > 0n ? n : null;
};

/**
 * Re-derives whichever of total, count and pack size was not among the two
 * typed last. A figure that would not come out exact is left blank, and the
 * card offers even splits instead.
 */
function relink(row: PackRow, precision: number): PackRow {
  if (!row.hasInner) return row;
  const typed = row.edits.slice(0, 2);
  const free: Linked | undefined = (['each', 'count', 'total'] as const).find((f) => !typed.includes(f));
  if (typed.length < 2 || !free) return row;
  const total = positive(row.qtyPerPackage);
  const each = positive(row.innerQty);
  const count = /^\s*\d+\s*$/.test(row.innerCount) ? BigInt(row.innerCount.trim()) : null;
  if (free === 'total') {
    return count !== null && each !== null ? { ...row, qtyPerPackage: fromTenThousandths(count * each) } : row;
  }
  if (free === 'count') {
    if (total === null || each === null) return row;
    const exact = total % each === 0n && total / each >= 2n && total / each <= BigInt(MAX_INNER_COUNT);
    return { ...row, innerCount: exact ? (total / each).toString() : '' };
  }
  if (total === null || count === null || count < 2n) return row;
  const exact = total % count === 0n && fitsPrecision(total / count, precision);
  return { ...row, innerQty: exact ? fromTenThousandths(total / count) : '' };
}

/** Ready-made sizes for an empty card, by what the item is counted in. */
function starters(family: UnitFamily, unit: string): { label: string; row: Partial<PackRow> }[] {
  const plain = (name: string, qty: string) => ({ label: `${name} of ${qty} ${unit}`, row: { name, qtyPerPackage: qty } });
  const inner = (name: string, count: string, innerName: string, each: string) => ({
    label: `${name}: ${count} × ${innerName} of ${each} ${unit}`,
    row: {
      name,
      qtyPerPackage: fromTenThousandths(BigInt(count) * toTenThousandths(each)!),
      hasInner: true,
      innerName,
      innerCount: count,
      innerQty: each,
    },
  });
  switch (family) {
    case 'weight':
      return [plain('Bag', '50'), plain('Bag', '25'), inner('Carton', '50', 'Pack', '1'), inner('Carton', '20', 'Pack', '0.5')];
    case 'volume':
      return [plain('Can', '5'), plain('Bottle', '1'), inner('Carton', '12', 'Bottle', '1'), inner('Carton', '20', 'Bottle', '0.5')];
    case 'dose':
      return [plain('Vial', '1000'), plain('Vial', '500'), inner('Box', '10', 'Vial', '1000')];
    case 'piece':
      return [plain('Tray', '30'), plain('Box', '10'), plain('Carton', '12')];
    default:
      // A bag, sack, drum, carton or box is already the outer package.
      if (/^(bag|bags|bori|sack|sacks|drum|drums|carton|cartons|ctn|box|boxes)$/i.test(unit)) return [];
      return [plain('Carton', '10'), plain('Carton', '20'), plain('Carton', '50')];
  }
}

const innerDefault = (family: UnitFamily) => (family === 'volume' ? 'Bottle' : family === 'dose' ? 'Vial' : 'Pack');

/** Package names worth suggesting when none of the items use one yet. */
const COMMON_NAMES = ['Bag', 'Carton', 'Box', 'Pack', 'Bottle', 'Can', 'Vial', 'Tray', 'Sack', 'Drum', 'Jar', 'Pouch', 'Sachet'];

/**
 * The Packing card of the item form: what one of the item's unit holds, and
 * the package sizes it comes in, each read as a sentence and checked as it is
 * typed by the same rules the server applies (packing.ts).
 */
export function PackingEditor({
  value,
  onChange,
  unit,
  contentUnits,
  contentUnitFor,
  nameSuggestions = [],
  disabled = false,
}: {
  value: PackingValue;
  onChange: (next: PackingValue) => void;
  /** Null until a unit of measure is chosen. */
  unit: CountingUnit | null;
  /** Units one of the item's unit can hold, for the "holds" picker. */
  contentUnits: SearchOption[];
  /** The unit an option names, so the rules can tell "holds kg" from "holds bags". */
  contentUnitFor: (id: string) => PackingUnit | undefined;
  nameSuggestions?: string[] | undefined;
  disabled?: boolean;
}) {
  const radioName = useId();
  const rows = value.packagings;
  const set = (packagings: PackRow[]) => onChange({ ...value, packagings });
  const update = (key: string, patch: Partial<PackRow>, edited?: Linked) =>
    set(
      rows.map((r) => {
        if (r.key !== key) return r;
        const edits = edited ? [edited, ...r.edits.filter((e) => e !== edited)] : r.edits;
        const next = { ...r, ...patch, edits };
        return edited && unit ? relink(next, unit.precision) : next;
      }),
    );
  const add = (row: Partial<PackRow> = {}) =>
    set([
      ...rows,
      {
        key: newKey(),
        name: '',
        qtyPerPackage: '',
        hasInner: false,
        innerName: '',
        innerCount: '',
        innerQty: '',
        isDefault: rows.length === 0,
        edits: row.hasInner ? ['count', 'each'] : [],
        ...row,
      },
    ]);
  const remove = (key: string) => {
    const left = rows.filter((r) => r.key !== key);
    // The default gone, the first one left takes over.
    if (left.length && !left.some((r) => r.isDefault)) left[0] = { ...left[0]!, isDefault: true };
    set(left);
  };

  const contentUnit = value.contentUomId ? contentUnitFor(value.contentUomId) : undefined;
  const context: PackingContext | null = unit ? { unit, contentUnit } : null;
  const names = [...new Set([...nameSuggestions, ...COMMON_NAMES])];
  const unitLabel = unit?.label ?? '';
  const content = toTenThousandths(value.contentQty);
  const contentLabel = contentUnit?.label ?? value.contentUomLabel;

  // "One BAG holds 50 KG" matters when the unit is a container; for kg or
  // litres it says nothing, so it stays out of the way unless already set.
  const holdsOpen =
    !!unit && (unit.family === 'container' || unit.family === 'other' || value.contentQty !== '' || value.contentUomId !== '');
  const holdsOffered = !!unit && unit.family === 'piece';
  const holdsIssue = context ? contentProblem(value.contentQty, value.contentUomId !== '', context) : null;
  const setProblem = context && rows.length > 1 ? setLevelProblem(rows, context) : null;

  return (
    <div className="packing">
      {!unit && <p className="settings__hint">Choose the unit of measure first: package sizes are counted in it.</p>}

      {holdsOpen && unit && (
        <div className="packing__holds">
          <Field
            label={`One ${unitLabel} holds`}
            type="number"
            min={0}
            value={value.contentQty}
            disabled={disabled}
            invalid={!!holdsIssue && value.contentQty !== ''}
            onChange={(contentQty) => onChange({ ...value, contentQty })}
            hint={
              holdsIssue && (value.contentQty !== '' || value.contentUomId !== '')
                ? <span className="hint--alert">{holdsIssue}</span>
                : `Optional. e.g. 50 for a ${unit.family === 'container' ? unitLabel.toLowerCase() : 'bag'} of 50 kg, so quantities can also be read in kg`
            }
          />
          <SearchSelect
            label="Of"
            value={value.contentUomId}
            selectedLabel={value.contentUomLabel || undefined}
            options={contentUnits}
            placeholder="Choose a unit…"
            disabled={disabled}
            clearable
            onChange={(option) =>
              onChange({ ...value, contentUomId: option?.value ?? '', contentUomLabel: option?.label ?? '' })
            }
          />
        </div>
      )}
      {!holdsOpen && holdsOffered && (
        <button
          type="button"
          className="btn btn--ghost btn--sm packing__more"
          disabled={disabled}
          onClick={() => onChange({ ...value, contentQty: '', contentUomId: contentUnits[0]?.value ?? '', contentUomLabel: contentUnits[0]?.label ?? '' })}
        >
          + Say what one {unitLabel} holds
        </button>
      )}

      {unit && rows.length === 0 && starters(unit.family, unitLabel).length === 0 && (
        <button type="button" className="btn btn--ghost btn--sm packing__more" disabled={disabled} onClick={() => add()}>
          + Add a package size
        </button>
      )}
      {unit && rows.length === 0 && starters(unit.family, unitLabel).length > 0 && (
        <div className="packing__starters">
          <span className="settings__hint">
            Add the packages it comes in, so stock can be typed and read as “3 Cartons”. Start from one of these, or add your own:
          </span>
          <div className="settings__choices">
            {starters(unit.family, unitLabel).map((s) => (
              <button key={s.label} type="button" className="chip" disabled={disabled} onClick={() => add(s.row)}>
                {s.label}
              </button>
            ))}
            <button type="button" className="chip" disabled={disabled} onClick={() => add()}>
              + Your own
            </button>
          </div>
        </div>
      )}

      {rows.map((row, index) => {
        const sent = packagingsToSend([row])[0]!;
        const issue = context ? packagingProblem([sent], context) : null;
        const touched = row.name !== '' || row.qtyPerPackage !== '';
        const total = positive(row.qtyPerPackage);
        const each = positive(row.innerQty);
        const split =
          row.hasInner && total !== null && each !== null && row.innerCount === '' && row.edits[0] !== 'count'
            ? nearestSplits(row.qtyPerPackage, row.innerQty)
            : null;
        const evenOnes =
          row.hasInner && total !== null && unit && row.innerQty === '' && /^\d+$/.test(row.innerCount.trim())
            ? evenCounts(row.qtyPerPackage, Number(row.innerCount), unit.precision)
            : null;
        const evenSizes = split && unit ? evenCounts(row.qtyPerPackage, Number(total! / each!), unit.precision) : null;
        const name = row.name.trim() || 'package';
        const innerName = row.innerName.trim() || 'pack';
        return (
          <div className="packing__row" key={row.key} role="group" aria-label={`Package size ${index + 1}`}>
            <div className="packing__main">
              <Field
                label="Package"
                value={row.name}
                disabled={disabled}
                maxLength={32}
                suggestions={names}
                placeholder="e.g. Carton"
                onChange={(n) => update(row.key, { name: n })}
              />
              <Field
                label={`Holds (${unitLabel})`}
                type="number"
                min={0}
                value={row.qtyPerPackage}
                disabled={disabled}
                onChange={(q) => update(row.key, { qtyPerPackage: q }, 'total')}
              />
              <div className="packing__side">
                {rows.length > 1 && (
                  <label className="settings__toggle packing__default">
                    <input
                      type="radio"
                      name={radioName}
                      checked={row.isDefault}
                      disabled={disabled}
                      onChange={() => set(rows.map((r) => ({ ...r, isDefault: r.key === row.key })))}
                    />
                    <span>Default</span>
                  </label>
                )}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm btn--danger"
                  disabled={disabled}
                  onClick={() => remove(row.key)}
                  aria-label={`Remove ${row.name.trim() || `package size ${index + 1}`}`}
                >
                  Remove
                </button>
              </div>
            </div>

            <label className="settings__toggle">
              <input
                type="checkbox"
                checked={row.hasInner}
                disabled={disabled}
                onChange={(e) =>
                  update(row.key, {
                    hasInner: e.target.checked,
                    ...(e.target.checked && !row.innerName ? { innerName: innerDefault(unit?.family ?? 'other') } : {}),
                    edits: row.qtyPerPackage ? ['total'] : [],
                  })
                }
              />
              <span>
                <strong>Has smaller packs inside</strong>
                <span className="dim"> — e.g. a carton of 50 packs of 1 kg</span>
              </span>
            </label>

            {row.hasInner && (
              <div className="packing__inner">
                <Field
                  label="How many"
                  type="number"
                  min={2}
                  value={row.innerCount}
                  disabled={disabled}
                  onChange={(c) => update(row.key, { innerCount: c }, 'count')}
                />
                <Field
                  label="Pack name"
                  value={row.innerName}
                  disabled={disabled}
                  maxLength={32}
                  suggestions={names}
                  onChange={(n) => update(row.key, { innerName: n })}
                />
                <Field
                  label={`Each holds (${unitLabel})`}
                  type="number"
                  min={0}
                  value={row.innerQty}
                  disabled={disabled}
                  onChange={(q) => update(row.key, { innerQty: q }, 'each')}
                />
              </div>
            )}

            {split ? (
              <div className="packing__fix" role="alert">
                <span>
                  {row.qtyPerPackage} {unitLabel} does not split evenly into {plural(innerName, 2)} of {row.innerQty} {unitLabel}.
                </span>
                <div className="settings__choices">
                  {split.fewer && (
                    <button
                      type="button"
                      className="chip"
                      onClick={() => update(row.key, { innerCount: String(split.fewer!.count), qtyPerPackage: split.fewer!.total, edits: ['count', 'each'] })}
                    >
                      {split.fewer.count} {plural(innerName, 2)} = {split.fewer.total} {unitLabel}
                    </button>
                  )}
                  <button
                    type="button"
                    className="chip"
                    onClick={() => update(row.key, { innerCount: String(split.more.count), qtyPerPackage: split.more.total, edits: ['count', 'each'] })}
                  >
                    {split.more.count} {plural(innerName, 2)} = {split.more.total} {unitLabel}
                  </button>
                  {[evenSizes?.below, evenSizes?.above].map(
                    (s) =>
                      s && (
                        <button
                          key={s.count}
                          type="button"
                          className="chip"
                          onClick={() => update(row.key, { innerCount: String(s.count), innerQty: s.each, edits: ['count', 'total'] })}
                        >
                          {s.count} {plural(innerName, s.count)} of {s.each} {unitLabel}
                        </button>
                      ),
                  )}
                </div>
              </div>
            ) : evenOnes ? (
              <div className="packing__fix" role="alert">
                <span>
                  {row.qtyPerPackage} {unitLabel} does not split into {row.innerCount} equal {plural(innerName, 2)}
                  {unit && unit.precision === 0 ? ` of whole ${unitLabel}` : ''}.
                </span>
                <div className="settings__choices">
                  {[evenOnes.below, evenOnes.above].map(
                    (s) =>
                      s && (
                        <button
                          key={s.count}
                          type="button"
                          className="chip"
                          onClick={() => update(row.key, { innerCount: String(s.count), innerQty: s.each, edits: ['count', 'total'] })}
                        >
                          {s.count} {plural(innerName, s.count)} of {s.each} {unitLabel}
                        </button>
                      ),
                  )}
                </div>
              </div>
            ) : issue && touched ? (
              <p className="packing__problem">{issue}</p>
            ) : (
              !issue && (
                <p className="packing__sum">
                  1 {name} ={' '}
                  {row.hasInner && `${row.innerCount} ${plural(innerName, Number(row.innerCount))} × ${row.innerQty} ${unitLabel} = `}
                  {row.qtyPerPackage} {unitLabel}
                  {content !== null && content > 0n && contentLabel && total !== null
                    ? ` = ${fromTenThousandths((total * content + 5000n) / 10000n)} ${contentLabel}`
                    : ''}
                </p>
              )
            )}
          </div>
        );
      })}

      {setProblem && <p className="packing__problem">{setProblem}</p>}

      {unit && rows.length > 0 && rows.length < MAX_PACKAGINGS && (
        <button type="button" className="btn btn--ghost btn--sm packing__more" disabled={disabled} onClick={() => add()}>
          + Add another package size
        </button>
      )}
    </div>
  );
}

/** Problems between rows: a name used twice, two defaults, or two sizes the same. */
function setLevelProblem(rows: readonly PackRow[], context: PackingContext): string | null {
  const sent = packagingsToSend(rows);
  // Each row's own problem shows beside it; here only what lies between them.
  if (sent.some((p) => packagingProblem([p], context) !== null)) return null;
  const problem = packagingProblem(sent, context);
  if (problem) return problem;
  for (let i = 0; i < sent.length; i++)
    for (let j = i + 1; j < sent.length; j++)
      if (toTenThousandths(sent[i]!.qtyPerPackage) === toTenThousandths(sent[j]!.qtyPerPackage))
        return `${sent[i]!.name} and ${sent[j]!.name} both hold ${sent[i]!.qtyPerPackage} ${context.unit.label}. That is allowed, but check it is meant.`;
  return null;
}
